export type TenantBranding = {
  brandName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  showPoweredByNextMoca: boolean;
};

export const DEFAULT_TENANT_BRANDING: TenantBranding = {
  brandName: "Next Moca",
  logoUrl: "/nextmoca-logo-text.png",
  faviconUrl: "/favicon.ico",
  primaryColor: "#B87333",
  secondaryColor: "#8A5525",
  accentColor: "#2F6F92",
  showPoweredByNextMoca: false,
};

export const NEXT_MOCA_APP_BAR_COLOR = "#000";

export const WHITE_LABEL_DEMO_BRANDING: TenantBranding = {
  brandName: "White Label Demo",
  logoUrl: "/white-label-demo-rocket.png",
  faviconUrl: "/white-label-demo-rocket.png",
  primaryColor: "#1A2E5A",
  secondaryColor: "#E87722",
  accentColor: "#4A90E2",
  showPoweredByNextMoca: true,
};

export type TenantBrandingInput = Partial<TenantBranding> | null | undefined;

export function isValidBrandColor(value: unknown): value is string {
  return typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value.trim());
}

export function normalizeBrandColor(value: unknown, fallback: string): string {
  return isValidBrandColor(value) ? value.trim().toUpperCase() : fallback;
}

function normalizeOptionalUrl(value: unknown, fallback: string | null): string | null {
  if (typeof value !== "string") {
    return fallback;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

export function normalizeTenantBranding(input: TenantBrandingInput): TenantBranding {
  const raw = input ?? {};
  const brandName =
    typeof raw.brandName === "string" && raw.brandName.trim()
      ? raw.brandName.trim()
      : DEFAULT_TENANT_BRANDING.brandName;

  return {
    brandName,
    logoUrl: normalizeOptionalUrl(raw.logoUrl, DEFAULT_TENANT_BRANDING.logoUrl),
    faviconUrl: normalizeOptionalUrl(raw.faviconUrl, DEFAULT_TENANT_BRANDING.faviconUrl),
    primaryColor: normalizeBrandColor(
      raw.primaryColor,
      DEFAULT_TENANT_BRANDING.primaryColor,
    ),
    secondaryColor: normalizeBrandColor(
      raw.secondaryColor,
      DEFAULT_TENANT_BRANDING.secondaryColor,
    ),
    accentColor: normalizeBrandColor(
      raw.accentColor,
      DEFAULT_TENANT_BRANDING.accentColor,
    ),
    showPoweredByNextMoca:
      typeof raw.showPoweredByNextMoca === "boolean"
        ? raw.showPoweredByNextMoca
        : DEFAULT_TENANT_BRANDING.showPoweredByNextMoca,
  };
}

export function resolveTenantBranding(
  persistedBranding: TenantBrandingInput,
  whiteLabelDemo = false,
): TenantBranding {
  if (whiteLabelDemo) {
    return WHITE_LABEL_DEMO_BRANDING;
  }
  return normalizeTenantBranding(persistedBranding);
}

export function isDefaultNextMocaBranding(
  branding: Pick<TenantBranding, "brandName" | "logoUrl">,
): boolean {
  const hasDefaultLogo = !branding.logoUrl || isDefaultNextMocaLogo(branding.logoUrl);

  return isNextMocaBrandName(branding.brandName) && hasDefaultLogo;
}

export function isDefaultNextMocaLogo(logoUrl: string | null | undefined): boolean {
  if (!logoUrl) {
    return false;
  }
  const logoPath = normalizeBrandAssetPath(logoUrl);
  const logoFileName = logoPath.split("/").pop() ?? "";
  return [
    "nextmoca-logo-text.png",
    "nextmoca-logotext.png",
    "nextmoca-logo.png",
    "next-moca-logo.png",
  ].includes(logoFileName);
}

export function getBrandAppBarColor(branding: TenantBranding): string {
  const shouldUseNextMocaAppBar =
    isNextMocaBrandName(branding.brandName) ||
    isDefaultPrimaryColor(branding.primaryColor);
  return shouldUseNextMocaAppBar
    ? NEXT_MOCA_APP_BAR_COLOR
    : branding.primaryColor;
}

function isDefaultPrimaryColor(primaryColor: string): boolean {
  return (
    normalizeBrandColor(primaryColor, DEFAULT_TENANT_BRANDING.primaryColor) ===
    normalizeBrandColor(DEFAULT_TENANT_BRANDING.primaryColor, "#000000")
  );
}

function isNextMocaBrandName(brandName: string): boolean {
  const normalizedBrandName = brandName.toLowerCase().replace(/[^a-z0-9]/g, "");
  const defaultBrandName = DEFAULT_TENANT_BRANDING.brandName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  return normalizedBrandName === defaultBrandName;
}

function normalizeBrandAssetPath(value: string | null): string {
  if (!value) {
    return "";
  }
  const trimmed = value.trim();
  try {
    return new URL(trimmed, "http://nextmoca.local").pathname.toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

type Rgb = {
  r: number;
  g: number;
  b: number;
};

function hexToRgb(hex: string): Rgb {
  const normalized = normalizeBrandColor(hex, DEFAULT_TENANT_BRANDING.primaryColor);
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function channelToHex(value: number): string {
  return Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, "0");
}

function rgbToHex({ r, g, b }: Rgb): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`.toUpperCase();
}

export function mixBrandColor(color: string, target: string, amount: number): string {
  const sourceRgb = hexToRgb(color);
  const targetRgb = hexToRgb(target);
  const boundedAmount = Math.max(0, Math.min(1, amount));
  return rgbToHex({
    r: sourceRgb.r + (targetRgb.r - sourceRgb.r) * boundedAmount,
    g: sourceRgb.g + (targetRgb.g - sourceRgb.g) * boundedAmount,
    b: sourceRgb.b + (targetRgb.b - sourceRgb.b) * boundedAmount,
  });
}

export function brandColorAlpha(color: string, alpha: number): string {
  const rgb = hexToRgb(color);
  const boundedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${boundedAlpha})`;
}

export function getBrandContrastText(color: string): "#17100B" | "#FFFFFF" {
  const { r, g, b } = hexToRgb(color);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#17100B" : "#FFFFFF";
}
